/** The composition point. Adding a page means touching one branch file, never this one. */

import { createBrowserRouter, type RouteObject } from 'react-router';

import { RootErrorBoundary } from '@/pages/errors/RootErrorBoundary';
import { ShellErrorBoundary } from '@/pages/errors/ShellErrorBoundary';

import { appRoutes } from './app.routes.js';
import { errorRoutes } from './error.routes.js';
import { publicRoutes } from './public.routes.js';
import { PermissionRoute } from './guards/PermissionRoute.js';
import { ProtectedRoute } from './guards/ProtectedRoute.js';
import { PublicOnlyRoute } from './guards/PublicOnlyRoute.js';

const routes: RouteObject[] = [
  {
    path: '/',
    ErrorBoundary: RootErrorBoundary,
    children: [
      { Component: PublicOnlyRoute, children: publicRoutes },
      {
        Component: ProtectedRoute,
        // A throw inside a screen still renders inside the shell, so the user keeps their
        // navigation rather than being dropped somewhere unfamiliar.
        ErrorBoundary: ShellErrorBoundary,
        children: [{ Component: PermissionRoute, children: appRoutes }],
      },
      ...errorRoutes,
    ],
  },
];

export const router = createBrowserRouter(routes);

/**
 * Every screen's code, fetched in the background once the app is idle, so the first click on a
 * menu item opens it at once instead of waiting on the network. One at a time and only when the
 * browser has nothing else to do: it must never compete with the screen being used. A failure is
 * ignored — the route's own `lazy` fetches it again when it is actually opened.
 */
function preloadScreens(queue: RouteObject[]): void {
  const next = queue.shift();
  if (next === undefined) return;

  const idle = (callback: () => void) =>
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(callback, { timeout: 2000 })
      : window.setTimeout(callback, 200);

  idle(() => {
    const load = typeof next.lazy === 'function' ? next.lazy() : Promise.resolve();
    void Promise.resolve(load)
      .catch(() => undefined)
      .finally(() => {
        preloadScreens(queue);
      });
  });
}

window.setTimeout(() => {
  preloadScreens([...appRoutes]);
}, 3000);
export type { RouteHandle } from './guards/PermissionRoute.js';
